import { Combobox, Input, SelectOption } from '@payaca/ambrosia';
import * as suppliersActions from '@payaca/store/suppliers/suppliersActions';
import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { mergeSelectedIntoOptions } from '@/helpers/selectOptionsHelper';
import useGetSupplier from '../../../api/queries/suppliers/useGetSupplier';
import useGetSuppliers from '../../../api/queries/suppliers/useGetSuppliers';
import CreateEditSupplierModal from '../createEditSupplierModal/CreateEditSupplierModal';

type ListedSupplier = {
  id: number;
  name: string;
};
interface Props {
  selectedSupplierId?: number;
  onChange: (selectedSupplierId?: number | null) => void;
  enableSupplierCreation?: boolean;
  excludeSystemManaged?: boolean;
  canChangeSupplier?: boolean;
  availableSupplierIds?: string[];
}
const SupplierSelectionControl: FC<Props> = ({
  selectedSupplierId,
  onChange,
  enableSupplierCreation = false,
  excludeSystemManaged = false,
  canChangeSupplier = true,
  availableSupplierIds,
}: Props): JSX.Element => {
  const [showCreateSupplierModal, setShowCreateSupplierModal] = useState(false);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState<string>();

  const { suppliers, refetch: refetchSuppliers } = useGetSuppliers(
    { searchTerm, managedBy: excludeSystemManaged ? ['SELF'] : undefined },
    { limit: 100 }
  );

  const { supplier: selectedSupplier } = useGetSupplier(
    selectedSupplierId ? `${selectedSupplierId}` : undefined
  );

  const supplierSelectOptions = useMemo(() => {
    const items = availableSupplierIds
      ? (suppliers?.items.filter((s) => availableSupplierIds.includes(s.id)) ??
        [])
      : (suppliers?.items ?? []);

    const supplierOptions: SelectOption<number, ListedSupplier>[] = items.map(
      (s) => ({
        value: +s.id,
        label: s.name,
        groupId: 'suppliers',
      })
    );

    // A selected supplier may fall outside the paginated results (e.g. >100
    // suppliers); merge it in so the Combobox can render its label.
    const withSelected = mergeSelectedIntoOptions(
      supplierOptions,
      selectedSupplier
        ? {
            value: +selectedSupplier.id,
            label: selectedSupplier.name,
            groupId: 'suppliers',
          }
        : undefined
    );

    if (enableSupplierCreation) {
      return [
        ...withSelected,
        { groupId: 'other', label: 'Add a new Supplier', value: -1 },
      ];
    }
    return withSelected;
  }, [
    suppliers,
    enableSupplierCreation,
    availableSupplierIds,
    selectedSupplier,
  ]);

  const supplierSelectOptionGroups = [
    { label: 'Suppliers', id: 'suppliers' },
    ...(enableSupplierCreation
      ? [
          {
            label: 'Other',
            id: 'other',
          },
        ]
      : []),
  ];

  const handleCreateSupplier = useCallback((supplierName: string) => {
    dispatch(
      suppliersActions.requestPersistSupplier(
        { name: supplierName },
        (supplierId: number) => {
          onChange(supplierId);
        }
      )
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'onChange', and 'dispatch'
  }, []);

  return (
    <>
      {canChangeSupplier ? (
        <Combobox
          value={selectedSupplierId}
          options={supplierSelectOptions}
          optionGroups={supplierSelectOptionGroups}
          filterFunction={null}
          query={searchTerm}
          setQuery={setSearchTerm}
          placeholder="Start typing to search for a Supplier..."
          inputPlaceholder="Start typing to search for a Supplier..."
          onOptionClick={(_e, option) => {
            if (option?.value === -1 && enableSupplierCreation) {
              if (searchTerm) {
                handleCreateSupplier(searchTerm);
              } else {
                setShowCreateSupplierModal(true);
              }
            } else {
              onChange(option?.value);
              setSearchTerm(undefined);
            }
          }}
        />
      ) : (
        <Input value={selectedSupplier?.name} disabled />
      )}

      <CreateEditSupplierModal
        isOpen={showCreateSupplierModal}
        onClose={() => setShowCreateSupplierModal(false)}
        onPersistSupplierSuccess={(supplierId: number) => {
          void refetchSuppliers();
          onChange(supplierId);
        }}
      />
    </>
  );
};

export default SupplierSelectionControl;
